import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch, RootState} from 'store/store';
import React, {useEffect, useState} from 'react';
import TextInput from 'components/inputs/TextInput';
import SubmitButton from 'components/SubmitButton';
import DescriptionInput from 'components/inputs/DescriptionInput';
import CustomSelect from 'components/inputs/CustomSelect';
import XIcon from 'assets/images/icons/XIcon';
import {buildDocumentTemplateElement} from 'utils/buildDocumentTemplateElement';
import {DocumentTemplateElementThunks} from './documentTemplateElementThunks';
import {setIsEditing} from './documentTemplateElementSlice';
import {DocumentTemplateElementOptions} from 'constants/documentTemplateElementOptions';

interface DocumentTemplateElementDrawerProps {
  id?: string;
  closeDrawer: () => void;
}

const DocumentTemplateElementDrawer: React.FC<
  DocumentTemplateElementDrawerProps
> = ({closeDrawer}) => {
  const [updatedDocumentTemplateElement, setUpdatedDocumentTemplateElement] =
    useState<any>({});

  const dispatch = useDispatch<AppDispatch>();
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const program = useSelector((state: RootState) => state.program.program);
  const documentTemplateElement = useSelector(
    (state: RootState) => state.documentTemplateElement.documentTemplateElement,
  );

  const pertainsToOptions = [
    {value: 'all', label: 'All programs for this client'},
    {value: 'program', label: 'Only this program'},
  ];

  const visibilityOptions = [
    {value: 'demographics', label: 'Demographics and Reports'},
    {value: 'report', label: 'Report Only'},
  ];

  useEffect(() => {
    if (documentTemplateElement !== undefined) {
      setUpdatedDocumentTemplateElement({
        ...documentTemplateElement,
        pertainsTo: documentTemplateElement.elementableId ? 'program' : 'all',
        visibility: documentTemplateElement.properties?.visibility || '',
        defaultValue: documentTemplateElement.properties?.defaultValue || '',
        name: documentTemplateElement.properties?.label || '',
        description: documentTemplateElement.properties?.description || '',
        type: documentTemplateElement.elementType,
      });
    } else {
      setUpdatedDocumentTemplateElement({});
    }
  }, [documentTemplateElement]);

  useEffect(() => {
    const isFormValid =
      updatedDocumentTemplateElement.name &&
      updatedDocumentTemplateElement.pertainsTo &&
      updatedDocumentTemplateElement.type &&
      updatedDocumentTemplateElement.visibility;
    setIsSaveDisabled(!isFormValid);
  }, [
    updatedDocumentTemplateElement.name,
    updatedDocumentTemplateElement.pertainsTo,
    updatedDocumentTemplateElement.type,
    updatedDocumentTemplateElement.visibility,
    updatedDocumentTemplateElement.defaultValue,
  ]);

  const handleChange = (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
      | {target: {id: string; value: any}},
  ) => {
    const {id, value} = e.target;
    setUpdatedDocumentTemplateElement({
      ...updatedDocumentTemplateElement,
      [id]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    const newElement = buildDocumentTemplateElement(
      updatedDocumentTemplateElement.type,
    );
    if (!newElement) {
      return;
    }

    newElement.elementableType = 'Program';
    newElement.elementableId =
      updatedDocumentTemplateElement.pertainsTo === 'all' ? null : program.id;
    newElement.properties = {
      defaultValue: updatedDocumentTemplateElement.defaultValue || '',
      description: updatedDocumentTemplateElement.description || '',
      label: updatedDocumentTemplateElement.name || '',
      visibility: updatedDocumentTemplateElement.visibility || '',
    };

    if (documentTemplateElement?.id) {
      newElement.id = documentTemplateElement.id;
      await dispatch(DocumentTemplateElementThunks.update(newElement));
    } else {
      await dispatch(DocumentTemplateElementThunks.create(newElement));
    }
    e.preventDefault();
    setUpdatedDocumentTemplateElement({});
    closeDrawer();
  };

  const handleClose = () => {
    dispatch(setIsEditing(false));
    setUpdatedDocumentTemplateElement({});
    closeDrawer();
  };

  return (
    <div className="flex flex-col h-screen">
      <div className="flex-1 flex flex-col">
        <div className="h-auto p-6 bg-sky-50 border-b border-sky-200 flex justify-between items-start gap-2 w-full">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="text-lg font-semibold tracking-normal text-cyan-800">
              {documentTemplateElement?.id
                ? 'Edit custom field'
                : 'Create new custom field'}
            </div>
            <div className="text-sm font-light tracking-normal leading-5 text-zinc-400">
              {documentTemplateElement?.id ? 'Edit ' : 'Create a new '}custom
              field and set field settings
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none pt-2">
            <XIcon />
          </button>
        </div>
        <div className="flex-1 px-3 pb-6 pt-3 overflow-y-auto">
          <div className="h-full flex flex-col overflow-y-auto overflow-hidden">
            <div className="h-full px-4">
              <form onSubmit={handleSubmit}>
                <div className="flex flex-col space-y-6 items-center mt-4">
                  <div className="w-full">
                    <TextInput
                      id="name"
                      type="text"
                      label="Name"
                      value={updatedDocumentTemplateElement.name || ''}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="w-full">
                    <DescriptionInput
                      id="description"
                      type="text"
                      label="Description"
                      value={updatedDocumentTemplateElement.description || ''}
                      onChange={handleChange}
                      optional="optional"
                    />
                  </div>
                  <div className="w-full">
                    <div className="flex flex-col gap-[0.625rem] w-full">
                      <label
                        htmlFor="pertainsTo"
                        className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                        Pertains to
                      </label>
                      <CustomSelect
                        options={pertainsToOptions}
                        value={updatedDocumentTemplateElement.pertainsTo}
                        onChange={selectedOption =>
                          handleChange({
                            target: {
                              id: 'pertainsTo',
                              value: selectedOption.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="w-full">
                    <div className="flex flex-col gap-[0.625rem] w-full">
                      <label
                        htmlFor="type"
                        className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                        Type
                      </label>
                      <CustomSelect
                        options={DocumentTemplateElementOptions}
                        value={updatedDocumentTemplateElement.type}
                        onChange={selectedOption =>
                          handleChange({
                            target: {
                              id: 'type',
                              value: selectedOption.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="w-full">
                    <div className="flex flex-col gap-[0.625rem] w-full">
                      <label
                        htmlFor="visibility"
                        className="w-full block grow shrink basis-0 text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
                        Visibility
                      </label>
                      <CustomSelect
                        options={visibilityOptions}
                        value={updatedDocumentTemplateElement.visibility}
                        onChange={selectedOption =>
                          handleChange({
                            target: {
                              id: 'visibility',
                              value: selectedOption.value,
                            },
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="w-full">
                    <TextInput
                      id="defaultValue"
                      type="text"
                      label="Default value"
                      value={updatedDocumentTemplateElement.defaultValue}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <footer className="flex-shrink-0 px-6 pb-4 pt-5 flex justify-end gap-5">
        <div className="border-t border-slate-200 w-full">
          <div className="flex w-full justify-center items-center gap-x-2 pb-4 pt-4">
            <SubmitButton
              onClick={handleSubmit}
              label={
                (documentTemplateElement?.id ? 'Save' : 'Create new') +
                ' custom field'
              }
              disabled={isSaveDisabled}
            />
          </div>
          <div className="flex w-full justify-center items-center gap-x-2 pb-4">
            <button
              type="button"
              onClick={handleClose}
              className="self-center text-sm font-light tracking-normal leading-5 text-center text-zinc-400 hover:text-cyan-400">
              Never mind
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocumentTemplateElementDrawer;
